import { useSelector } from "react-redux";
import LineChart from "@cloudscape-design/components/line-chart";
import BarChart from "@cloudscape-design/components/bar-chart";
import { useSecurityData } from './ApiCalls/SecurityDataProvider'; 
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import EventDetailsModal from './DesignComponents/EventDetailsModal';
import QueryEngine from "./QueryEnginePage";
import {Shield,Globe,Network, Workflow,Database,Container,Users,Server,Key,ChevronLeft,ChevronRight,BrickWall,  Loader, ContactRound, Icon
} from "lucide-react";
import { bucket } from '@lucide/lab';

const apiUrl = process.env.REACT_APP_API_URL;
const SecurityMonitoringDashboard = (props) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [showQueryEngine, setShowQueryEngine] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  let accessToken = useSelector((state) => state.accessToken.value);
  const scrollContainerRef = useRef(null);
  const { region, accountName, duration, loginData,isLoading,lastUpdatedTime } = props;
  const navigate = useNavigate();
  const { securityData } = useSecurityData();
  const { s3Data, lbData, ec2Data, rdsData, ecsData, kmsData , wafData} = securityData;
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const checkScrollability = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 5
      );
    }
  };

  useEffect(() => {
    checkScrollability();
    window.addEventListener("resize", checkScrollability);
    return () => window.removeEventListener("resize", checkScrollability);
  }, []);


  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const cardWidth = 300;
      const gap = 16;
      const scrollDistance = cardWidth + gap;

      const targetScroll =
        container.scrollLeft +
        (direction === "left" ? -scrollDistance : scrollDistance);

      container.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });

      setTimeout(checkScrollability, 300);
    }
  };


  const getDateRange = () => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    
    const startDate = new Date(endDate);
    
    switch(duration) {
      case '7 days':
        startDate.setDate(endDate.getDate() - 6);
        break;
      case '30 days':
        startDate.setDate(endDate.getDate() - 29);
        break;
      default:
        startDate.setHours(0, 0, 0, 0);
    }
    
    return { startDate, endDate };
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const generateAllDatesInRange = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);
    
    const endDateCopy = new Date(endDate);
    endDateCopy.setHours(23, 59, 59, 999);
    
    while (currentDate <= endDateCopy) {
      dates.push(formatDateKey(new Date(currentDate)));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const processLoginData = () => {
    if (!loginData?.data) return [];
    
    const { startDate, endDate } = getDateRange();
    const allDates = generateAllDatesInRange(startDate, endDate);
    
    let dataPoints = [];
    
    if (duration === '1 day') {
      dataPoints = generateHourlyDataPoints(startDate, endDate);
    } else {
      dataPoints = allDates.map(dateKey => ({
        dateKey,
        successful: 0,
        failed: 0
      }));
    }
    
    if (loginData.data && Array.isArray(loginData.data)) {
      loginData.data.forEach(event => {
        if (!event.eventtime) return;
        
        const eventDate = new Date(event.eventtime);
        if (eventDate >= startDate && eventDate <= endDate) {
          let index;
          
          if (duration === '1 day') {
            const hour = eventDate.getHours();
            index = dataPoints.findIndex(point => 
              point.hour === hour
            );
          } else {
            const dateKey = formatDateKey(eventDate);
            index = dataPoints.findIndex(point => point.dateKey === dateKey);
          }
          
          if (index !== -1) {
            if (event.Status === 'Success') {
              dataPoints[index].successful++;
            } else {
              dataPoints[index].failed++;
            }
          }
        }
      });
    }
    let maxSuccessful = 0;
    let maxFailed = 0;
    
    dataPoints.forEach(data => {
      maxSuccessful = Math.max(maxSuccessful, data.successful);
      maxFailed = Math.max(maxFailed, data.failed);
    });
    
    let successfulData, failedData;
    
    if (duration === '1 day') {
      successfulData = dataPoints.map(point => ({
        x: point.timestamp,
        y: point.successful
      }));
      
      failedData = dataPoints.map(point => ({
        x: point.timestamp,
        y: point.failed
      }));
    } else {
      successfulData = dataPoints.map(point => ({
        x: new Date(point.dateKey),
        y: point.successful
      }));
      
      failedData = dataPoints.map(point => ({
        x: new Date(point.dateKey),
        y: point.failed
      }));
    }
    
    return {
      series: [
        {
          title: "Successful Logins",
          type: "line",
          data: successfulData,
          valueFormatter: (value) => `${value}`,
          color: "#A3C2E6"
        },
        {
          title: "Failed Attempts",
          type: "line",
          data: failedData,
          valueFormatter: (value) => `${value}`,
          color: "#E6EEF7"
        }
      ],
      maxValue: Math.max(maxSuccessful, maxFailed)
    };
  };
  const generateHourlyDataPoints = (startDate, endDate) => {
    const dataPoints = [];
    for (let hour = 0; hour < 24; hour++) {
      const timestamp = new Date(startDate);
      timestamp.setHours(hour, 0, 0, 0);
      
      dataPoints.push({
        hour,
        timestamp,
        successful: 0,
        failed: 0
      });
    }
    
    return dataPoints;
  };
  const processResourceChanges = () => {
    return [
      {
        title: "Created",
        type: "bar",
        data: [
          { x: "EC2", y: ec2Data?.summary?.instanceOperations?.runCount || 0 },
          { x: "RDS", y: rdsData?.summary?.createCount || 0 },
          { x: "S3", y: s3Data?.summary?.createBucketCount || 0 },
          { x: "ECS", y: (ecsData?.summary?.clusterOperations?.createCount || 0) + (ecsData?.summary?.serviceOperations?.createCount || 0) },
          { x: "LB", y: lbData?.summary?.loadBalancerOperations?.createCount || 0 },
          { x: "KMS", y: kmsData?.summary?.createKeyCount || 0 },
          {x: "WAF", y:(wafData?.summary?.webACLOperations?.createCount || 0) + (wafData?.summary?.ruleOperations?.createCount || 0) + (wafData?.summary?.ruleGroupOperations?.createCount || 0) + (wafData?.summary?.apiKeyOperations?.createCount || 0) }
        ],
        valueFormatter: (value) => `${value}`,
        color: "#99CCFF"
      },
      {
        title: "Deleted",
        type: "bar",
        data: [
          { x: "EC2", y: ec2Data?.summary?.instanceOperations?.terminateCount || 0 },
          { x: "RDS", y: rdsData?.summary?.deleteCount || 0 },
          { x: "S3", y: s3Data?.summary?.deleteBucketCount || 0 },
          { x: "ECS", y: (ecsData?.summary?.clusterOperations?.deleteCount || 0)+(ecsData?.summary?.serviceOperations?.deleteCount || 0)  },
          { x: "LB", y: lbData?.summary?.loadBalancerOperations?.deleteCount || 0 },
          { x: "KMS", y: kmsData?.summary?.scheduleKeyDeletionCount || 0 },
          {x: "WAF", y:(wafData?.summary?.webACLOperations?.deleteCount || 0)+(wafData?.summary?.ruleOperations?.deleteCount || 0) + (wafData?.summary?.ruleGroupOperations?.deleteCount || 0) + (wafData?.summary?.apiKeyOperations?.deleteCount || 0) }
        ],
        valueFormatter: (value) => `${value}`,
        color: "#B8D8FF"
      },
      {
        title: "Modified",
        type: "bar",
        data: [
          { x: "EC2", y: (ec2Data?.summary?.instanceOperations?.stopCount || 0) + 
                          (ec2Data?.summary?.instanceOperations?.startCount || 0) + 
                          (ec2Data?.summary?.instanceOperations?.rebootCount || 0) },
          { x: "RDS", y: rdsData?.summary?.modifyCount || 0 },
          { x: "S3", y: (s3Data?.summary?.putBucketPolicyCount || 0) + 
                         (s3Data?.summary?.putBucketAclCount || 0) + 
                         (s3Data?.summary?.putBucketEncryptionCount || 0) + 
                         (s3Data?.summary?.putBucketLoggingCount || 0) + 
                         (s3Data?.summary?.putBucketVersioningCount || 0) },
                         { x: "ECS", y: (ecsData?.summary?.clusterOperations?.updateCount || 0)+(ecsData?.summary?.serviceOperations?.updateCount || 0) },
          { x: "LB", y: lbData?.summary?.loadBalancerOperations?.modifyCount || 0 },
          { x: "KMS", y: (kmsData?.summary?.enableKeyCount || 0) + 
                          (kmsData?.summary?.disableKeyCount || 0) },
          {x: "WAF", y:  (wafData?.summary?.webACLOperations?.updateCount || 0) +
                         (wafData?.summary?.ruleOperations?.updateCount || 0) + 
                         (wafData?.summary?.ruleGroupOperations?.updateCount || 0) + 
                         (wafData?.summary?.apiKeyOperations?.updateCount || 0) }

        ],
        valueFormatter: (value) => `${value}`,
        color: "#D6E9FF"
      }
    ];
  };

  const { startDate, endDate } = getDateRange();
  const loginDataProcessed = processLoginData();

  const userActivityData = {
    series: loginDataProcessed.series,
    xDomain: [startDate, endDate],
    yDomain: [0, Math.max(10, loginDataProcessed.maxValue * 1.2)]
  };

  const resourceChangesData = {
    series: processResourceChanges(),
    xDomain: ["EC2", "RDS", "S3", "ECS","LB","KMS","WAF"],
    yDomain: [0, processResourceChanges().reduce((max, seriesItem) => {
      const seriesMax = seriesItem.data.reduce((sMax, dataPoint) => {
        return Math.max(sMax, dataPoint.y);
      }, 0);
      return Math.max(max, seriesMax);
    }, 0) + 10]
  };

  const calculateEcsTotalEvents = () => {
    if (!ecsData?.summary) return 0;
    
    const { clusterOperations, serviceOperations, taskOperations } = ecsData.summary;
    
    const clusterTotal = (
      (clusterOperations?.createCount || 0) +
      (clusterOperations?.deleteCount || 0) +
      (clusterOperations?.updateCount || 0)
    );
    
    const serviceTotal = (
      (serviceOperations?.createCount || 0) +
      (serviceOperations?.deleteCount || 0) +
      (serviceOperations?.updateCount || 0)
    );
    
    const taskTotal = (
      (taskOperations?.registerCount || 0) +
      (taskOperations?.deregisterCount || 0) +
      (taskOperations?.runCount || 0) +
      (taskOperations?.startCount || 0) +
      (taskOperations?.stopCount || 0)
    );
    
    return clusterTotal + serviceTotal + taskTotal;
  };
  const calculateWafTotalEvents = () => {
    if (!wafData?.summary) return 0;
    
    const { webACLOperations, ruleOperations, ruleGroupOperations,apiKeyOperations} = wafData.summary;
    
    const webACLTotal = (
      (webACLOperations?.createCount || 0) +
      (webACLOperations?.deleteCount || 0) +
      (webACLOperations?.updateCount || 0)
    );
    
    const ruleTotal = (
      (ruleOperations?.createCount || 0) +
      (ruleOperations?.deleteCount || 0) +
      (ruleOperations?.updateCount || 0)
    );
    const ruleGroupTotal = (
      (ruleGroupOperations?.createCount || 0) +
      (ruleGroupOperations?.deleteCount || 0) +
      (ruleGroupOperations?.updateCount || 0)
    );
    const apiKeyTotal = (
      (apiKeyOperations?.createCount || 0) +
      (apiKeyOperations?.deleteCount || 0) +
      (apiKeyOperations?.updateCount || 0)
    );
    
    
    return webACLTotal + ruleTotal + ruleGroupTotal + apiKeyTotal ;
  };

  const resourceMetrics = [
    {
      title: "EC2",
      total:
        (ec2Data?.summary?.instanceOperations?.runCount || 0) +
        (ec2Data?.summary?.instanceOperations?.startCount || 0) +
        (ec2Data?.summary?.instanceOperations?.stopCount || 0) +
        (ec2Data?.summary?.instanceOperations?.terminateCount || 0),
      Created: ec2Data?.summary?.instanceOperations?.runCount || 0,
      started: ec2Data?.summary?.instanceOperations?.startCount || 0,
      stopped: ec2Data?.summary?.instanceOperations?.stopCount || 0,
      terminated: ec2Data?.summary?.instanceOperations?.terminateCount || 0,
      trend: `Last ${duration == "1 day" ? "24 hours" : `${duration}`}`,
      icon: <Container className="h-6 w-6 text-[#003366]" />,
      path: "/security/ec2",
    },
    {
      title: "IAM User Activity",
      total: loginData?.total_logins || 0,
      successful: (loginData?.total_logins || 0) - (loginData?.summary?.failed_login_count || 0),
      failed: loginData?.summary?.failed_login_count || 0,
      IAM: loginData?.summary?.console_login_count || 0,
      SAML: loginData?.summary?.api_login_count || 0,
      trend: `Last ${duration == "1 day" ? "24 hours" : `${duration}`}`,
      icon:<ContactRound className="h-6 w-6 text-[#003366]" />,
      path: "/security/loginData",
    },
    {
      title: "Load Balancers",
      total: lbData?.data?.length || 0,
      created: lbData?.summary?.loadBalancerOperations?.createCount || 0,
      deleted: lbData?.summary?.loadBalancerOperations?.deleteCount || 0,
      modified: lbData?.summary?.loadBalancerOperations?.modifyCount || 0,
      errors: lbData?.summary?.errorCount || 0,
      trend: `Last ${duration === "1 day" ? "24 hours" : duration}`,
      icon:<Workflow className="h-6 w-6 text-[#003366]" />,
      path: "/security/loadBalancer",
    },
    {
      title: "WAF",
      total: calculateWafTotalEvents(),
      created:
        (wafData?.summary?.webACLOperations?.createCount || 0) +
        (wafData?.summary?.ruleOperations?.createCount || 0) +
        (wafData?.summary?.ruleGroupOperations?.createCount || 0) +
        (wafData?.summary?.apiKeyOperations?.createCount || 0),
      deleted:
        (wafData?.summary?.webACLOperations?.deleteCount || 0) +
        (wafData?.summary?.ruleOperations?.deleteCount || 0) +
        (wafData?.summary?.ruleGroupOperations?.deleteCount || 0) +
        (wafData?.summary?.apiKeyOperations?.deleteCount || 0),
      updated:
        (wafData?.summary?.webACLOperations?.updateCount || 0) +
        (wafData?.summary?.ruleOperations?.updateCount || 0) +
        (wafData?.summary?.ruleGroupOperations?.updateCount || 0) +
        (wafData?.summary?.apiKeyOperations?.updateCount || 0),
      trend: `Last ${duration == "1 day" ? "24 hours" : `${duration}`}`,
      icon: <BrickWall className="h-6 w-6 text-[#003366]" />,
      path: "/security/waf"
    },
    {
      title: 'S3',
      total: s3Data?.data?.length || 0,
      created: s3Data?.summary?.createBucketCount || 0,
      deleted: s3Data?.summary?.deleteBucketCount || 0,
      Policy: (s3Data?.summary?.putBucketPolicyCount || 0) + (s3Data?.summary?.deleteBucketPolicyCount || 0),
      security: (s3Data?.summary?.putBucketAclCount || 0) + 
                       (s3Data?.summary?.putPublicAccessBlockCount || 0) + 
                       (s3Data?.summary?.deletePublicAccessBlockCount || 0),
      trend: `Last ${duration === "1 day" ? "24 hours" : `${duration}`}`,
      // icon:<Globe className="h-6 w-6  text-[#003366]" />,
      icon: <Icon iconNode={bucket} className="h-6 w-6  text-[#003366]"/>,
      path: '/security/s3'
    },
    {
      title: 'RDS',
      total: rdsData?.data?.length || 0,
      created: rdsData?.summary?.createCount || 0,
      deleted: rdsData?.summary?.deleteCount || 0,
      started: rdsData?.summary?.startCount || 0,
      stopped: rdsData?.summary?.stopCount || 0,
      trend: `Last ${duration=="1 day"?"24 hours":`${duration}`}`,
      icon: <Database className="h-6 w-6 text-[#003366]" />,
      path: '/security/rds'
    },

    {
      title: 'ECS',
      total: calculateEcsTotalEvents(),
      clusters: (ecsData?.summary?.clusterOperations?.createCount || 0) +
                (ecsData?.summary?.clusterOperations?.deleteCount || 0) +
                (ecsData?.summary?.clusterOperations?.updateCount || 0),
      services: (ecsData?.summary?.serviceOperations?.createCount || 0) + 
                (ecsData?.summary?.serviceOperations?.deleteCount || 0) + 
                (ecsData?.summary?.serviceOperations?.updateCount || 0),
      tasks: (ecsData?.summary?.taskOperations?.registerCount || 0) + 
             (ecsData?.summary?.taskOperations?.deregisterCount || 0) + 
             (ecsData?.summary?.taskOperations?.runCount || 0) +
             (ecsData?.summary?.taskOperations?.startCount || 0) +
             (ecsData?.summary?.taskOperations?.stopCount || 0),
      trend: `Last ${duration === "1 day" ? "24 hours" : `${duration}`}`,
      icon: <Server className="h-6 w-6 text-[#003366]" />,
      path: '/security/ecs'
    },

    {
      title: 'KMS',
      total: kmsData?.data?.length || 0,
      created: kmsData?.summary?.createKeyCount || 0,
      enabled: kmsData?.summary?.enableKeyCount || 0,
      disabled: kmsData?.summary?.disableKeyCount || 0,
      deleted: kmsData?.summary?.scheduleKeyDeletionCount || 0,
      trend: `Last ${duration=="1 day"?"24 hours":`${duration}`}`,
      icon: <Key className="h-6 w-6 text-[#003366]" />,
      path: '/security/kms'
    }
  ];
  const handleCardClick = (path) => {
    const stateMap = {
      '/security/s3': { s3Data },
      '/security/loadBalancer': { lbData },
      '/security/loginData': { loginData },
      '/security/ec2': { ec2Data },
      '/security/rds': { rdsData },
      '/security/ecs': { ecsData },
      '/security/kms': { kmsData },
      '/security/waf':{wafData}
    };
  
    navigate(path, {
      state: {
        ...stateMap[path],
        region,
        accountName,
        duration
      }
    });
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };
  
  const formatDateDisplay = (date) => {
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };
  
  const getEmptyState = () => (
    <div className="flex justify-center items-center h-40">
      <div className="text-center text-gray-500">
        {duration === '1 day' ? 
          'No login activity recorded in the last 24 hours' : 
          'No data available for the selected time period'}
      </div>
    </div>
  );

  const LoadingCardSkeleton = () => (
    <div className="flex-none w-[300px] bg-white rounded-lg shadow p-6 overflow-hidden relative">
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/70 to-transparent animate-shimmer" 
           style={{ backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }}></div>
      
      <div className="flex items-center justify-between mb-4">
        <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
        <div className="h-6 w-24 bg-gray-200 rounded"></div>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="h-10 w-16 bg-gray-200 rounded"></div>
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    </div>
  );

  const LoadingChartSkeleton = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
      <div className="h-[300px] bg-gray-100 rounded relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/70 to-transparent animate-shimmer"
             style={{ backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }}></div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-200 to-transparent"></div>
        <div className="absolute top-1/3 left-0 right-0 h-1 bg-gray-200"></div>
        <div className="absolute top-2/3 left-0 right-0 h-1 bg-gray-200"></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 p-6 bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-8 w-8 text-[#003366]" />
          <h1 className="text-2xl font-bold text-[#003366]">
            AWS Service Insights
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <button 
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              showQueryEngine ? 'bg-[#003366] text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => setShowQueryEngine(!showQueryEngine)}
          >
            {showQueryEngine ? 'Hide Query Engine' : 'Show Query Engine'}
          </button>
          <div className="text-sm text-gray-500 flex items-center gap-2">
            {isLoading && (
              <Loader className="h-4 w-4 text-blue-500 animate-spin" />
            )}
            Last updated: {lastUpdatedTime||"Unknown"}
          </div>
        </div>
      </div>
     <style jsx>{`
  @keyframes shimmer {
    0% { background-position: -100% 0; }
    100% { background-position: 100% 0; }
  }
  .animate-shimmer {
    animation: shimmer 5s infinite;
  }
  .pulse-animation {
    animation: pulse 1.5s infinite;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`}</style>

      <div
        className="relative group"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {canScrollLeft && (
          <button
            onClick={() => scroll("left")}
            className={`absolute -left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full 
                      bg-white/80 shadow-lg hover:bg-white/90 transition-all duration-300
                      backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:scale-110
                      ${isHovering ? "opacity-100" : "opacity-0"}`}
            style={{ transform: "translate(-50%, -50%)" }}
          >
            <ChevronLeft className="h-6 w-6 text-gray-600" />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto gap-4 scroll-smooth px-4 no-scrollbar"
          onScroll={checkScrollability}
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            scrollBehavior: "smooth",
          }}
        >
          {isLoading ? (
            [...Array(8)].map((_, index) => (
              <LoadingCardSkeleton key={index} />
            ))
          ) : (
            resourceMetrics.map((metric, index) => (
              <div
                key={index}
                className="flex-none w-[300px] bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => metric.path && handleCardClick(metric.path)}
              >
                <div className="flex items-center justify-between mb-4">
                  {metric.icon}
                  <h3 className="text-lg font-medium">{metric.title}</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-4xl font-bold text-center text-[#003366]"> 
                      {metric.total}
                    </span>
                    <span className="text-sm text-gray-500">{metric.trend}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(metric).map(([key, value]) => {
                      if (
                        key !== "title" &&
                        key !== "total" &&
                        key !== "trend" &&
                        key !== "icon" &&
                        key !== "path"
                      ) {
                        return (
                          <div
                            key={key}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded"
                          >
                            <span className="capitalize">{key}:</span>
                            <span className="font-medium">{value}</span>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {canScrollRight && (
          <button
            onClick={() => scroll("right")}
            className={`absolute -right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full 
                      bg-white/80 shadow-lg hover:bg-white/90 transition-all duration-300
                      backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:scale-110
                      ${isHovering ? "opacity-100" : "opacity-0"}`}
            style={{ transform: "translate(50%, -50%)" }}
          >
            <ChevronRight className="h-6 w-6 text-gray-600" />
          </button>
        )}
      </div>

      {showQueryEngine && <QueryEngine 
        region={region}
        accountName={accountName}
        duration={duration}
        accessToken={accessToken}
        handleEventClick={handleEventClick}
      />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {isLoading ? (
          <>
            <LoadingChartSkeleton />
            <LoadingChartSkeleton />
          </>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">
                {duration === '1 day' ? 
                  `User Activity (${formatDateDisplay(startDate)} - Hourly)` : 
                  `User Activity: Last ${duration}`}
              </h2>

              <LineChart
                series={userActivityData.series}
                height={300}
                xDomain={userActivityData.xDomain}
                yDomain={userActivityData.yDomain}
                xScaleType="time"
                statusType="finished"
                emphasizeBaselineAxis={true}
                hideLegend={false}
                empty={getEmptyState()}
                ariaLabel="User activity line chart"
                i18nStrings={{
                  filterLabel: "Filter user activity data",
                  filterPlaceholder: "Filter data",
                  filterSelectedAriaLabel: "selected",
                  legendAriaLabel: "Legend",
                  chartAriaRoleDescription: "line chart",
                  xTickFormatter: (date) => {
                    if (duration === '1 day') {
                      const hours = date.getHours();
                      return `${hours}:00`;
                    } else {
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }
                  }
                }}
              />
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Resource Changes (Last {duration})</h2>
              <BarChart
                series={resourceChangesData.series}
                height={300}
                xDomain={resourceChangesData.xDomain}
                yDomain={resourceChangesData.yDomain}
                statusType="finished"
                empty={
                  <div className="flex justify-center items-center h-40">
                    <div className="text-center text-gray-500">No data available</div>
                  </div>
                }
                ariaLabel="Resource changes bar chart"
                i18nStrings={{
                  filterLabel: "Filter resource changes data",
                  filterPlaceholder: "Filter data",
                  filterSelectedAriaLabel: "selected",
                  legendAriaLabel: "Legend",
                  chartAriaRoleDescription: "bar chart"
                }}
              />
            </div>
          </>
        )}
      </div>
      <EventDetailsModal 
        isOpen={isModalOpen} 
        event={selectedEvent} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default SecurityMonitoringDashboard;